/**
 * Utility functions for handling images in the application
 */

/**
 * Get the correct image path based on the environment and product type
 * 
 * @param {string} category - The image category (e.g. 'products', 'brand')
 * @param {string} productType - The product type (e.g. 'business', 'auto')
 * @param {string} position - The image position or variant (e.g. 'left', 'right', 'icon')
 * @param {string} extension - The file extension (default: 'svg')
 * @returns {string} The full image path
 */
export function getImagePath(category, productType, position, extension = 'svg') {
  // Add timestamp for cache busting
  const timestamp = new Date().getTime();
  
  // Format position to match the file naming convention (capitalize first letter)
  const formattedPosition = position.charAt(0).toUpperCase() + position.slice(1);
  
  // Use new path format (preferred)
  const subCategory = category === 'products' ? 'hero' : '';
  
  // Get standardized product type
  const standardizedType = getStandardizedProductType(productType);
  
  // Determine the appropriate naming convention based on category
  if (category === 'products') {
    return `/images/${category}/${subCategory}/${standardizedType}/hero-${standardizedType}-${position}.${extension}?v=${timestamp}`;
  } else if (category === 'productHeroBanners') {
    // Support legacy paths during transition
    return `/images/${category}/${productType}/${productType}HeroBanner${formattedPosition}.${extension}?v=${timestamp}`;
  } else {
    return `/images/${category}/${standardizedType}/${category}-${standardizedType}-${position}.${extension}?v=${timestamp}`;
  }
}

/**
 * Maps old product type names to new standardized names
 * 
 * @param {string} oldProductType - The old product type name (e.g. 'businessinsurance')
 * @returns {string} The standardized product type name (e.g. 'business')
 */
export function getStandardizedProductType(oldProductType) {
  const productTypeMapping = {
    'autoinsurance': 'auto',
    'homeinsurance': 'home',
    'lifeinsurance': 'life',
    'businessinsurance': 'business',
    'umbrellainsurance': 'umbrella',
    'floodinsurance': 'flood',
    'petinsurance': 'pet',
    'medicare': 'medicare',
    'commercialinsurance': 'commercial'
  };
  
  return productTypeMapping[oldProductType] || oldProductType;
}

/**
 * Attempt to load an image with fallbacks to different formats/locations
 * 
 * @param {HTMLImageElement} imgElement - The image DOM element
 * @param {Object} options - Options for loading the image
 * @param {string} options.category - The image category
 * @param {string} options.productType - The product type
 * @param {string} options.position - The image position
 * @param {string} options.extension - The initial file extension
 * @param {Function} options.onSuccess - Callback for successful load
 * @param {Function} options.onError - Callback for failed load after all attempts
 */
export function loadImageWithFallbacks(imgElement, options) {
  const { category, productType, position, extension = 'svg', onSuccess, onError } = options;
  
  // Counter for tracking fallback attempts
  let attempts = 0;
  const maxAttempts = 5;
  
  // Current extension and path type to try
  let currentExtension = extension;
  let useNewPath = true;
  
  // Get standardized product type for new path format
  const standardizedProductType = getStandardizedProductType(productType);
  
  // Function to handle image error and try fallbacks
  const handleError = () => {
    attempts++;
    console.log(`Image load attempt ${attempts} failed for ${productType} ${position}`);
    
    if (attempts >= maxAttempts) {
      console.error(`All image fallbacks failed for ${productType} ${position}`);
      if (onError) onError();
      return;
    }
    
    // Try different fallback strategies based on attempt number
    if (attempts === 1) {
      // First fallback: Try with capitalized position (L + capitalized position)
      const capitalizedPosition = 'L' + position.charAt(0).toUpperCase() + position.slice(1);
      if (category === 'products') {
        imgElement.src = `/images/products/hero/${standardizedProductType}/hero-${standardizedProductType}-${capitalizedPosition}.${currentExtension}?v=${new Date().getTime()}`;
      } else {
        imgElement.src = `/images/${category}/${standardizedProductType}/${category}-${standardizedProductType}-${capitalizedPosition}.${currentExtension}?v=${new Date().getTime()}`;
      }
      return;
    } else if (attempts === 2) {
      // Second fallback: Try PNG instead of SVG with new path
      if (currentExtension === 'svg') {
        currentExtension = 'png';
        if (category === 'products') {
          imgElement.src = `/images/products/hero/${standardizedProductType}/hero-${standardizedProductType}-${position}.${currentExtension}?v=${new Date().getTime()}`;
        } else {
          imgElement.src = `/images/${category}/${standardizedProductType}/${category}-${standardizedProductType}-${position}.${currentExtension}?v=${new Date().getTime()}`;
        }
        return;
      }
    } else if (attempts === 3) {
      // Third fallback: Try old path format with productHeroBanners
      useNewPath = false;
      currentExtension = 'svg';
      imgElement.src = `/images/productHeroBanners/${productType}/${productType}HeroBanner${position.charAt(0).toUpperCase() + position.slice(1)}.${currentExtension}?v=${new Date().getTime()}`;
      return;
    } else if (attempts === 4) {
      // Fourth fallback: Try PNG with old path format
      currentExtension = 'png';
      imgElement.src = `/images/productHeroBanners/${productType}/${productType}HeroBanner${position.charAt(0).toUpperCase() + position.slice(1)}.${currentExtension}?v=${new Date().getTime()}`;
      return;
    }
    
    // If we get here, we've exhausted our options
    if (onError) onError();
  };
  
  // Add event listeners for load/error
  imgElement.addEventListener('error', handleError);
  imgElement.addEventListener('load', () => {
    if (onSuccess) onSuccess();
  });
  
  // Initial load attempt with new path format
  if (category === 'products') {
    imgElement.src = `/images/products/hero/${standardizedProductType}/hero-${standardizedProductType}-${position}.${currentExtension}?v=${new Date().getTime()}`;
  } else {
    imgElement.src = `/images/${category}/${standardizedProductType}/${category}-${standardizedProductType}-${position}.${currentExtension}?v=${new Date().getTime()}`;
  }
  
  console.log(`Initial image load attempt: ${imgElement.src}`);
}

/**
 * Create a standardized image name based on our naming convention
 * 
 * @param {string} category - Image category (e.g. 'hero', 'icon')
 * @param {string} productType - Product type (e.g. 'business', 'auto')
 * @param {string} position - Image position (e.g. 'left', 'right')
 * @param {string} extension - File extension (default: 'svg')
 * @returns {string} Standardized image filename
 */
export function createStandardImageName(category, productType, position, extension = 'svg') {
  return `${category}-${productType}-${position}.${extension}`;
} 